import * as THREE from 'three';
import Experience from '../Experience.js';

export default class RemoteCursors {

    static FADE_DURATION = 3000;
    static LERP_SPEED = 12;

    constructor() {
        this.experience = new Experience();
        this.camera = this.experience.camera;
        this.canvas = this.experience.canvas;
        this.eventBus = this.experience.eventBus;
        this.selfInstanceId = this.experience.instance_id;

        this._container = document.createElement('div');
        this._container.id = 'remote-cursors';
        Object.assign(this._container.style, {
            position: 'absolute',
            top: '0', left: '0',
            width: '100%', height: '100%',
            pointerEvents: 'none',
            zIndex: '40',
            overflow: 'visible',
        });

        // Attach to the nearest positioned ancestor
        let parent = this.canvas.parentElement;
        while (parent && getComputedStyle(parent).position === 'static') {
            parent = parent.parentElement;
        }
        (parent || this.canvas.parentElement).appendChild(this._container);

        this.cursors = new Map();
        this._worldPos = new THREE.Vector3();
        this._cursorRef = this.eventBus.onRemote('cursor_moved', (data) => this.onCursorMoved(data));
    }

    onCursorMoved(data) {
        const instanceId = data.instance_id;
        if (instanceId === this.selfInstanceId) return;

        let cursor = this.cursors.get(instanceId);
        if (!cursor) {
            cursor = this._createCursor(data.color);
            this.cursors.set(instanceId, cursor);
        }

        cursor.targetX = data.x;
        cursor.targetY = data.y;
        cursor.targetZ = data.z;
        cursor.lastUpdate = performance.now();
        cursor.visible = true;
        cursor.el.style.display = 'block';
        cursor.opacity = 1.0;
    }

    _createCursor(colorStr) {
        const el = document.createElement('div');
        Object.assign(el.style, {
            position: 'absolute',
            width: '24px',
            height: '28px',
            backgroundColor: colorStr,
            clipPath: 'polygon(0% 0%, 0% 100%, 70% 75%)',
            border: '2px solid white',
            borderRadius: '2px',
            filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5)) drop-shadow(0 0 1px white)',
            display: 'none',
            willChange: 'transform',
        });
        this._container.appendChild(el);

        return {
            el,
            currentX: 0, currentY: 0, currentZ: 0,
            targetX: 0, targetY: 0, targetZ: 0,
            lastUpdate: 0,
            visible: false,
            opacity: 1.0
        };
    }

    update(delta) {
        const now = performance.now();
        const cam = this.camera.instance;
        const rect = this.canvas.getBoundingClientRect();
        const contRect = this._container.getBoundingClientRect();
        const lerpFactor = Math.min(1, RemoteCursors.LERP_SPEED * delta);
        const fadeSpeed = 1.0 / (RemoteCursors.FADE_DURATION / 1000);

        for (const [userId, cursor] of this.cursors) {
            if (!cursor.visible) continue;

            cursor.currentX += (cursor.targetX - cursor.currentX) * lerpFactor;
            cursor.currentY += (cursor.targetY - cursor.currentY) * lerpFactor;
            cursor.currentZ += (cursor.targetZ - cursor.currentZ) * lerpFactor;

            this._worldPos.set(cursor.currentX, cursor.currentY, cursor.currentZ);
            this._worldPos.project(cam);

            const screenX = (this._worldPos.x * 0.5 + 0.5) * rect.width + (rect.left - contRect.left);
            const screenY = (-this._worldPos.y * 0.5 + 0.5) * rect.height + (rect.top - contRect.top);

            cursor.el.style.transform = `translate(${screenX}px, ${screenY}px)`;

            const elapsed = now - cursor.lastUpdate;
            if (elapsed > RemoteCursors.FADE_DURATION) {
                cursor.opacity -= fadeSpeed * delta;
                if (cursor.opacity <= 0) {
                    cursor.opacity = 0;
                    cursor.visible = false;
                    cursor.el.style.display = 'none';
                }
            }

            cursor.el.style.opacity = cursor.opacity;
        }
    }

    removeCursor(userId) {
        const cursor = this.cursors.get(userId);
        if (!cursor) return;
        cursor.el.remove();
        this.cursors.delete(userId);
    }

    destroy() {
        this.eventBus.offRemote('cursor_moved', this._cursorRef);
        for (const [userId] of this.cursors) {
            this.removeCursor(userId);
        }
        this._container.remove();
    }
}
